import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApisService } from '@core/services/apis.service';
import { LanguageService } from '@core/services/language.service';
import { LoadingService } from '@core/services/loading.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-articles-details',
  templateUrl: './articles-details.component.html',
  styleUrls: ['./articles-details.component.scss']
})
export class ArticlesDetailsComponent implements OnInit {
  public currentPage = 1;
  public categories: any;
  public totalItems = 0;
  public articleDetails: any;
  public articleId: string;
  public fakeUsers: any[] = [];
  public selectedFakeUserId = '';
  public newComment = '';
  public lang: string;
  public actionLoading = false;
  public likedUsers: any[] = [];
  public likedUsersPagination: any;

  constructor(
    private apisService: ApisService,
    private route: ActivatedRoute,
    private loading: LoadingService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private language: LanguageService
  ) {}

  ngOnInit(): void {
    this.language.currentLang.subscribe((res: string) => {
      this.lang = res;
    });

    const preselectedUserId = this.route.snapshot.queryParamMap.get('fakeUserId');
    if (preselectedUserId) {
      this.selectedFakeUserId = preselectedUserId;
    }

    this.loadFakeUsers();

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.articleId = id;
      this.getArticleDetails(this.articleId);
      this.getArticleLikes(this.articleId);
    }
  }

  loadFakeUsers(): void {
    this.apisService.getArticleFormData().subscribe({
      next: (res) => {
        this.fakeUsers = res?.data?.fake_users || [];
      },
      error: () => {
        this.showError();
      }
    });
  }

  getArticleDetails(id: any): void {
    this.loading.show();
    this.apisService.getArticlesDetails(id).subscribe({
      next: (res) => {
        this.articleDetails = res.data;
        this.loading.hide();
      },
      error: (err) => {
        console.log('error in fetching article details', err);
        this.showError();
        this.loading.hide();
      }
    });
  }

  handleImageError(event): void {
    event.target.src = 'assets/images/icons/logo.jpg';
  }

  getArticleLikes(id: number | string, page: number = 1, append: boolean = false): void {
    this.apisService.getArticleLikes(id, page).subscribe({
      next: (res) => {
        const users = res?.data || [];
        this.likedUsers = append ? this.likedUsers.concat(users) : users;
        this.likedUsersPagination = res?.pagination || null;
      },
      error: (err) => {
        console.log('error in fetching article likes', err);
        this.likedUsers = [];
        this.likedUsersPagination = null;
      }
    });
  }

  loadMoreLikedUsers(): void {
    if (!this.likedUsersPagination?.has_more) {
      return;
    }

    this.getArticleLikes(this.articleId, this.likedUsersPagination.current_page + 1, true);
  }

  deleteFakeUser(id): void {
    this.apisService.deleteComments(id).subscribe({
      next: () => {
        this.translate.get([
          'MENU.DASHBOARD.SUCCESS',
          'MENU.DASHBOARD.COMMENT_DELETED'
        ]).subscribe(translations => {
          this.toastr.success(translations['MENU.DASHBOARD.COMMENT_DELETED'], translations['MENU.DASHBOARD.SUCCESS']);
        });
        this.getArticleDetails(this.articleId);
        this.getArticleLikes(this.articleId);
      },
      error: (err) => {
        console.log('error in fetching', err);
        this.showError();
      }
    });
  }

  addComment(): void {
    if (!this.ensureFakeUserSelected() || !this.newComment.trim()) {
      this.showValidationError();
      return;
    }

    this.actionLoading = true;
    this.apisService.addArticleComment(this.articleId, {
      fake_user_id: this.selectedFakeUserId,
      comment: this.newComment.trim()
    }).subscribe({
      next: () => {
        this.newComment = '';
        this.actionLoading = false;
        this.translate.get([
          'MENU.DASHBOARD.SUCCESS',
          'MENU.DASHBOARD.COMMENT_ADDED'
        ]).subscribe(translations => {
          this.toastr.success(translations['MENU.DASHBOARD.COMMENT_ADDED'], translations['MENU.DASHBOARD.SUCCESS']);
        });
        this.getArticleDetails(this.articleId);
        this.getArticleLikes(this.articleId);
      },
      error: (err) => {
        console.log('error in add comment', err);
        this.actionLoading = false;
        this.showError();
      }
    });
  }

  addLike(): void {
    if (!this.ensureFakeUserSelected()) {
      this.showValidationError();
      return;
    }

    this.actionLoading = true;
    this.apisService.addArticleLike(this.articleId, this.selectedFakeUserId).subscribe({
      next: () => {
        this.actionLoading = false;
        this.translate.get([
          'MENU.DASHBOARD.SUCCESS',
          'MENU.DASHBOARD.ARTICLE_REACTION_UPDATED'
        ]).subscribe(translations => {
          this.toastr.success(translations['MENU.DASHBOARD.ARTICLE_REACTION_UPDATED'], translations['MENU.DASHBOARD.SUCCESS']);
        });
        this.getArticleDetails(this.articleId);
        this.getArticleLikes(this.articleId);
      },
      error: (err) => {
        console.log('error in add like', err);
        this.actionLoading = false;
        this.showError();
      }
    });
  }

  addDislike(): void {
    if (!this.ensureFakeUserSelected()) {
      this.showValidationError();
      return;
    }

    this.actionLoading = true;
    this.apisService.addArticleDislike(this.articleId, this.selectedFakeUserId).subscribe({
      next: () => {
        this.actionLoading = false;
        this.translate.get([
          'MENU.DASHBOARD.SUCCESS',
          'MENU.DASHBOARD.ARTICLE_REACTION_UPDATED'
        ]).subscribe(translations => {
          this.toastr.success(translations['MENU.DASHBOARD.ARTICLE_REACTION_UPDATED'], translations['MENU.DASHBOARD.SUCCESS']);
        });
        this.getArticleDetails(this.articleId);
        this.getArticleLikes(this.articleId);
      },
      error: (err) => {
        console.log('error in add dislike', err);
        this.actionLoading = false;
        this.showError();
      }
    });
  }

  private ensureFakeUserSelected(): boolean {
    return !!this.selectedFakeUserId;
  }

  private showValidationError(): void {
    this.translate.get(['MENU.DASHBOARD.ERROR', 'MENU.DASHBOARD.FILL_REQUIRED_FIELDS']).subscribe(translations => {
      this.toastr.error(translations['MENU.DASHBOARD.FILL_REQUIRED_FIELDS'], translations['MENU.DASHBOARD.ERROR']);
    });
  }

  private showError(): void {
    this.translate.get(['MENU.DASHBOARD.ERROR', 'MENU.DASHBOARD.UNEXPECTED_ERROR']).subscribe(translations => {
      this.toastr.error(translations['MENU.DASHBOARD.UNEXPECTED_ERROR'], translations['MENU.DASHBOARD.ERROR']);
    });
  }
}
